/**
 * Fisher-Yates (Knuth) shuffle.
 * Returns a NEW shuffled array — does NOT mutate the input.
 */
export function fisherYatesShuffle<T>(array: readonly T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * xmur3 string hash (MurmurHash3 finalizer mix).
 * Returns a generator of 32-bit unsigned seeds derived from the input string.
 * Integer-only ops (Math.imul, >>>) — deterministic across JS engines.
 */
function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}

/**
 * mulberry32 PRNG. Returns a function producing floats in [0, 1).
 * Integer-only ops — deterministic across JS engines.
 */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Deterministic Fisher-Yates (Knuth) shuffle seeded by a string key.
 * Same seedKey + same input length ⇒ same permutation, on every platform
 * (Node, jsdom, browsers). Returns a NEW array — does NOT mutate the input.
 */
export function seededFisherYatesShuffle<T>(array: readonly T[], seedKey: string): T[] {
  const random = mulberry32(xmur3(seedKey)());
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}
